/**
 * The way into a rave's admin, from wherever the rave is.
 *
 * *"if user has admin rights on this rave show gear icon to access the rave
 * admin — on cards, list views, and on the rave app header."*
 *
 * **The gear works out for itself whether it should exist.** It could have
 * taken a `canAdmin` prop, and then My raves, Find a rave, the table, the
 * dashboard and the rave app would each have had to fetch the managed list
 * and pass it down — five places to add it and five places to forget. Asking
 * one shared query means a rave you administer grows a gear everywhere it is
 * drawn, including screens written later.
 *
 * **`/me/managed-raves` answers with an empty list for everybody else**, so a
 * visitor renders nothing, learns nothing, and pays one cached request for
 * it. The gear is a shortcut, never a permission: `radm.` checks the grant at
 * the API on every route behind it.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { leaveTo } from '@/app/AuthProvider';
import { raveAdminUrl } from '@/lib/tenant';
import { useT } from '@/i18n';

/**
 * What this file needs of `/me/managed-raves`.
 *
 * **Deliberately the endpoint's own shape**, `{ raves }` and not a rewrap.
 * Several places share the key `['me','managed-raves']`, and React Query
 * caches by key alone: re-wrapping the response as `{ festivals }` here once
 * meant whichever caller ran first won and the other read a missing field —
 * the drawer said "You are not attending anything yet" beside a grid of three
 * raves. A shared key must share a SHAPE.
 */
interface ManagedRaveRef {
  editionId: string;
  subdomain: string;
  editionSlug: string;
  role: 'FESTIVAL_ADMIN' | 'CONTENT_MANAGER';
}

export function useManagedRaves() {
  return useQuery({
    queryKey: ['me', 'managed-raves'],
    queryFn: () => api.get<{ raves: ManagedRaveRef[] }>('/me/managed-raves'),
    // It changes when somebody is given the keys to a festival, which is not
    // often, and this is asked on every card on every screen.
    staleTime: 10 * 60_000,
    retry: false,
  });
}

/**
 * The admin address for one edition, or null if this person has no business
 * there.
 */
export function useRaveAdminLink(editionId: string | undefined): string | null {
  const { data } = useManagedRaves();
  if (!editionId) return null;
  const mine = data?.raves?.find((r) => r.editionId === editionId);
  return mine ? raveAdminUrl(`/${mine.subdomain}/${mine.editionSlug}`) : null;
}

/**
 * The gear.
 *
 * `size` matches whatever it sits among: 38 is the header's round controls,
 * 30 suits a card corner, 26 a table row. Anything smaller stops being a
 * touch target, which is why the card and table variants put the gear in a
 * row of real buttons rather than floating it over artwork.
 */
export function RaveAdminGear({
  editionId, size = 30, label, onArtwork,
}: {
  editionId: string | undefined;
  size?: number;
  label?: string;
  /**
   * Floating over a photograph rather than sitting on a panel.
   *
   * `var(--card)` is white at 4.5% — a wash that reads on the
   * app's ground and disappears completely on a bright cover. Over artwork it
   * needs its own darkness, the same 68% ground + blur the countdown
   * pill beside it uses, or the gear is a rumour on half the raves.
   */
  onArtwork?: boolean;
}) {
  const tr = useT();
  const href = useRaveAdminLink(editionId);
  if (!href) return null;
  const name = label ?? tr('Manage this rave');

  return (
    <a
      href={href}
      aria-label={name}
      title={name}
      onClick={(e) => {
        /*
         * A card is usually inside a link to the rave, and a table row is
         * next to one. Without stopping this, clicking the gear also follows
         * whatever encloses it and lands on the festival app instead.
         */
        e.preventDefault();
        e.stopPropagation();
        void leaveTo(href);
      }}
      className={onArtwork ? 'app-gear is-on-art' : 'app-gear'}
      style={{ width: size, height: size }}
    >
      <svg
        width={Math.round(size * 0.52)}
        height={Math.round(size * 0.52)}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    </a>
  );
}
